import React from 'react';
import {
  Table,
  notification,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import { EQUIPMENT_CATEGORY_ID } from '../helpers/costcodeHelpers';
import { currencyFormatter } from '../helpers/inputParsers';

const NOTIFICATION_KEY = 'COST_CODE_UPLOAD_ERROR_NOTIFICATION';

const REQUIRED_COLUMNS = [
  { title: 'Name', dataIndex: 'name' },
  { title: 'Code', dataIndex: 'code' },
  { title: 'Description', dataIndex: 'description' },
];

const EXTENDED_COLUMNS = [
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'categoryPreview',
    render: (val) => val,
  },
  {
    title: 'Hourly Wage',
    dataIndex: 'hourlyWage',
    key: 'hourlyWagePreview',
    render: (val) => (typeof val === 'number' ? currencyFormatter(val) : val),
    isNumber: true,
  },
  {
    title: 'Daily Wage',
    dataIndex: 'dailyWage',
    key: 'dailyWagePreview',
    render: (val) => (typeof val === 'number' ? currencyFormatter(val) : val),
    isNumber: true,
  },
  {
    title: 'Wage Adjustment',
    dataIndex: 'wageAdjustment',
    key: 'wageAdjustmentPreview',
    render: (val) => (typeof val === 'number' ? currencyFormatter(val) : val),
    isNumber: true,
  },
  {
    title: 'Wage Multiplier',
    dataIndex: 'wageMultiplier',
    key: 'wageMultiplierPreview',
    render: (val) => (typeof val === 'number' ? `${val}x` : val),
    isNumber: true,
  },
  {
    title: 'Hourly Billing Rate',
    dataIndex: 'hourlyBillingRate',
    key: 'hourlyBillingRatePreview',
    render: (val) => (typeof val === 'number' ? currencyFormatter(val) : val),
    isNumber: true,
  },
  {
    title: 'Daily Billing Rate',
    dataIndex: 'dailyBillingRate',
    key: 'dailyBillingRatePreview',
    render: (val) => (typeof val === 'number' ? currencyFormatter(val) : val),
    isNumber: true,
  },
];

const isEmptyCell = (value) => (
  value === undefined
  || value === null
  || (typeof value === 'string' && !value.trim())
);

const openNotification = (description) => {
  notification.open({
    key: NOTIFICATION_KEY,
    message: 'Cost Codes Error',
    description,
    duration: 0,
    icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
  });
};

const getCustomFieldColumns = ({
  customFieldTemplate,
  mappedColumnSet,
  customFieldResponseMap,
}) => (
  customFieldTemplate?.sections?.flatMap(({ fields = [] }) => (
    fields
      .filter((field) => (
        field.type === 'text'
        && mappedColumnSet.has(`custom_${field.id}`)
      ))
      .map((field) => {
        const dataIndex = `custom_${field.id}`;

        return {
          title: field.title,
          dataIndex,
          key: `${dataIndex}Preview`,
          render: (_, record) => customFieldResponseMap?.[record.key]?.[field.id],
        };
      })
  )) ?? []
);

export default class CostCodeUploadPreview extends React.Component {
  constructor(props) {
    super(props);

    this.codeMap = {};
    this.nameMap = {};
  }

  componentDidMount() {
    this.checkError();
  }

  componentDidUpdate(prevProps) {
    const {
      shouldValidate: prevShouldValidate,
      dataSource: prevDataSource,
      mappedColumns: prevMappedColumns,
    } = prevProps;
    const {
      shouldValidate,
      dataSource,
      mappedColumns,
    } = this.props;

    if (
      prevDataSource !== dataSource
      || prevMappedColumns !== mappedColumns
      || (!prevShouldValidate && shouldValidate)
    ) {
      this.checkError();
    }
  }

  getInputColumn({
    conflictMap = {},
    value,
  }) {
    const lookupKey = value ? String(value).trim().toLowerCase() : (value ?? '');
    const hasConflict = conflictMap[lookupKey] > 1;
    return (
      <div
        style={{
          color: hasConflict ? 'red' : 'inherit',
          paddingLeft: 10,
        }}
      >
        {value}
      </div>
    );
  }

  checkError() {
    const {
      actionButtonEnabled,
      dataSource = [],
      mappedColumns = [],
      shouldValidate,
    } = this.props;

    if (!shouldValidate) return;

    this.codeMap = {};
    this.nameMap = {};

    const mappedColumnSet = new Set(mappedColumns);
    const requiredColumns = REQUIRED_COLUMNS
      .filter(({ dataIndex }) => !mappedColumnSet.has(dataIndex));

    const missingColumnValues = REQUIRED_COLUMNS
      .filter(({ dataIndex }) => dataSource.some((record) => isEmptyCell(record[dataIndex])));

    dataSource.forEach((data) => {
      const trimmedName = data.name ? String(data.name).trim().toLowerCase() : data.name;
      if (trimmedName) {
        this.nameMap[trimmedName] = (this.nameMap[trimmedName] || 0) + 1;
      }
    
      const trimmedCode = data.code ? String(data.code).trim().toLowerCase() : data.code;
      if (trimmedCode) {
        this.codeMap[trimmedCode] = (this.codeMap[trimmedCode] || 0) + 1;
      }
    });

    const nameClash = Object.keys(this.nameMap).filter((name) => name && this.nameMap[name] > 1);
    const codeClash = Object.keys(this.codeMap).filter((code) => code && this.codeMap[code] > 1);

    const hasHourlyWage = mappedColumnSet.has('hourlyWage');
    const hasDailyWage = mappedColumnSet.has('dailyWage');
    const hasWageAdjustment = mappedColumnSet.has('wageAdjustment');
    const hasWageMultiplier = mappedColumnSet.has('wageMultiplier');
    const hasHourlyBillingRate = mappedColumnSet.has('hourlyBillingRate');
    const hasDailyBillingRate = mappedColumnSet.has('dailyBillingRate');
    
    const hasWageRate = hasHourlyWage || hasDailyWage;
    const hasWageModifier = hasWageAdjustment || hasWageMultiplier;

    const hasEquipmentCostCode = dataSource.some((data) => (
      data.categoryIds?.includes(EQUIPMENT_CATEGORY_ID)
    ));
    
    const mappedColumnConflicts = [
      hasHourlyWage && hasDailyWage && !hasEquipmentCostCode
        ? 'Hourly Wage and Daily Wage cannot both be mapped'
        : null,
      hasHourlyBillingRate && hasDailyBillingRate
        ? 'Hourly Billing Rate and Daily Billing Rate cannot both be mapped'
        : null,
      hasWageRate && hasWageModifier
        ? 'Hourly/Daily Wage and Wage Adjustment or Wage Multiplier cannot both be mapped'
        : null,
    ].filter(Boolean);

    if (
      !requiredColumns.length
      && !missingColumnValues.length
      && !nameClash.length
      && !codeClash.length
      && !mappedColumnConflicts.length
    ) {
      notification.close(NOTIFICATION_KEY);
      actionButtonEnabled(true);
      return;
    }

    const errorMessage = (
      <div>
        The following cost code errors were found:
        <br />
        <br />
        {requiredColumns.length ? (
          <div>
            Missing Required Columns:
            <p style={{ color: 'red' }}>{requiredColumns.map(({ title }) => title).join(', ')}</p>
            <br />
          </div>
        ) : null}
        {missingColumnValues.length ? (
          <div>
            Missing Values:
            <p style={{ color: 'red' }}>{missingColumnValues.map(({ title }) => title).join(', ')}</p>
            <br />
          </div>
        ) : null}
        {nameClash.length ? (
          <div>
            Name Conflicts:
            <p style={{ color: 'red' }}>{nameClash.join(', ')}</p>
            <br />
          </div>
        ) : null}
        {codeClash.length ? (
          <div>
            Code Conflicts:
            <p style={{ color: 'red' }}>{codeClash.join(', ')}</p>
            <br />
          </div>
        ) : null}
        {mappedColumnConflicts.length ? (
          <div>
            Mapped Column Conflicts:
            <p style={{ color: 'red' }}>{mappedColumnConflicts.join(', ')}</p>
            <br />
          </div>
        ) : null}
        Please edit the file to fix these values and try re-uploading.
      </div>
    );

    openNotification(errorMessage);
    actionButtonEnabled(false);
  }

  render() {
    const {
      dataSource = [],
      mappedColumns = [],
      scroll = {},
      customFieldTemplate,
      customFieldResponseMap,
    } = this.props;

    const mappedColumnSet = new Set(mappedColumns);
    const extendedColumns = EXTENDED_COLUMNS.filter(({ dataIndex }) => (
      mappedColumnSet.has(dataIndex)
    ));
    const customFieldColumns = getCustomFieldColumns({
      customFieldTemplate,
      mappedColumnSet,
      customFieldResponseMap,
    });

    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name) => (
          this.getInputColumn({
            conflictMap: this.nameMap,
            value: name,
          })
        ),
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        render: (code) => (
          this.getInputColumn({
            conflictMap: this.codeMap,
            value: code,
          })
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (description) => (
          this.getInputColumn({
            value: description,
          })
        ),
      },
      ...extendedColumns,
      ...customFieldColumns,
    ];

    return (
      <Table
        size="small"
        columns={columns}
        dataSource={dataSource.map((item) => ({
          ...item,
          key: item.id ?? item.key,
        }))}
        pagination={false}
        style={{ marginBottom: 50 }}
        scroll={scroll}
      />
    );
  }
}

CostCodeUploadPreview.propTypes = {
  actionButtonEnabled: PropTypes.func.isRequired,
  customFieldTemplate: PropTypes.shape({
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        sectionId: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
      })),
    })),
  }),
  customFieldResponseMap: PropTypes.shape({}),
  dataSource: PropTypes.arrayOf(PropTypes.shape({})),
  mappedColumns: PropTypes.arrayOf(PropTypes.string),
  shouldValidate: PropTypes.bool,
  scroll: PropTypes.shape({}),
};

CostCodeUploadPreview.defaultProps = {
  customFieldTemplate: null,
  customFieldResponseMap: {},
  dataSource: [],
  mappedColumns: [],
  shouldValidate: false,
  scroll: {},
};
